import React, { useRef, useState, useEffect } from "react"
import { t } from "i18next"
import { useNavigate } from "react-router-dom";
import {
   CheckOutlined,
   DeleteFilled,
   EditFilled,
   PlusOutlined,
   RedoOutlined,
   HistoryOutlined,
   CopyOutlined
} from "@ant-design/icons";
import moment from "moment";
import {
   getSalaryScaleByYear,
   createAndUpdateSalaryScaleRow,
   deleteSalaryScaleRow
} from "services/salary_scale_record";
import {
   InputText,
   SelectInput,
   Breadcrumb,
   Button,
   Col,
   Input,
   Result,
   Row,
   Space,
   Spin
} from "common/elements";
import { Util } from "common/util";
import DuplicateDrawer from "./DuplicateDrawer";
import HistoryDrawer from "./HistoryDrawer";
import dataSourceYear from "./SelectYear";
import AttachmentModal from "./AttachmentModal";

export default function Index() {
   const [data, setData] = useState({
      minColumns: [],
      midColumns: [],
      maxColumns: [],
   });
   const [originalScale, setOriginalScale] = useState({});
   const [salaryScales, setSalaryScales] = useState([])
   const [presalaryScales, setPresalaryScales] = useState([])
   const [loading, setLoading] = useState(false);
   const [loadingTable, setLoadingTable] = useState(false)
   const [loadingBtn, setLoadingBtn] = useState(false);
   const [year, setYear] = useState(parseInt(moment().format("YYYY")));
   const params = new URLSearchParams(document.location.search);
   const navigate = useNavigate();
   const modalRef = useRef(null);
   const drawerRef = useRef(null);
   const attachmentRef = useRef(null);

   const fetchData = (year) => {
      setLoadingTable(true);
      getSalaryScaleByYear(year)
         .then((response) => {
            if (response?.data?.data) {
               const data = response.data.data;
               
               setData(data);

               setSalaryScales(data.salaryScales.map((salaryScale) => ({
                  id: salaryScale.id,
                  level: salaryScale.level,
                  jobTitles: salaryScale.jobTitles,
                  grade: salaryScale.grade,
                  minPayload: JSON.parse(salaryScale.minPayload),
                  midPayload: JSON.parse(salaryScale.midPayload),
                  maxPayload: JSON.parse(salaryScale.maxPayload),
                  order: salaryScale.order
               })));

            } else {
               setData({
                  minColumns: [],
                  midColumns: [],
                  maxColumns: []
               });
            }
         })
         .finally(() => {
            setLoading(false);
            setLoadingTable(false);
         })
   }

   const onChangeYear = (value) => {
      setYear(value);
      fetchData(value);

      params.set("year", value);
      Util.getInstance().pushParamsToURL("/salary-scale-record", params.toString());
   }

   const onAddNew = () => {
      if (Util.getInstance().hasPermission("create", "salary_scale_record")) {
         navigate(`/salary-scale-record/create/${year}`)
      } else {
         Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      }
   }

   const onNew = () => {
      if (Util.getInstance().hasPermission("create", "salary_scale_record")) {
         const property = {
            key: Date.now() + Math.random(),
            new: true,
            level: null,
            jobTitles: null,
            grade: null,
            minPayload: data.minColumns.map((_, index) => ({ [index + 1]: null })),
            midPayload: data.midColumns.map((_, index) => ({ [index + 1]: null })),
            maxPayload: data.maxColumns.map((_, index) => ({ [index + 1]: null })),
            order: null
         }
         salaryScales.push(property);
         setSalaryScales([...salaryScales])
      } else {
         Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      }
   }

   const onUpdateButton = (index, salaryScale) => {
      if (Util.getInstance().hasPermission("edit", "salary_scale_record")) {
         const minPayloadStr = JSON.stringify(salaryScale.minPayload);
         const midPayloadStr = JSON.stringify(salaryScale.midPayload);
         const maxPayloadStr = JSON.stringify(salaryScale.maxPayload);

         salaryScale.minPayload = minPayloadStr;
         salaryScale.midPayload = midPayloadStr;
         salaryScale.maxPayload = maxPayloadStr;

         salaryScales[index]["new"] = true;

         setPresalaryScales((preState) => {
            return [...preState, {
               id: salaryScale.id,
               level: salaryScale.level,
               jobTitles: salaryScale.jobTitles,
               grade: salaryScale.grade,
               minPayload: JSON.parse(salaryScale.minPayload),
               midPayload: JSON.parse(salaryScale.midPayload),
               maxPayload: JSON.parse(salaryScale.maxPayload),
               order: salaryScale.order
            }]
         });
         
         setSalaryScales([...salaryScales]);

         setOriginalScale(`${minPayloadStr}${midPayloadStr}${maxPayloadStr}`);
      } else {
         Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      }
   }

   const onDeleteButton = (index) => {
      if (Util.getInstance().hasPermission("delete", "salary_scale_record")) {
         Util.getInstance()
            .sweetAlertConfirm(t("text_confrim"), "You won't be able to revert this!", ["Cancel", "Yes, delete it!"])
            .then((willDelete) => {
               if (willDelete) {
                  salaryScales[index]["status"] = 3;
                  deleteSalaryScaleRow(salaryScales[index]).then(() => {
                     fetchData(year)
                     setPresalaryScales([]);
                  });
               }
            });
      } else {
         Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      }
   }

   const onSaveButton = (index) => {
      const edittingRow = salaryScales[index];
      const minPayloadStr = JSON.stringify(edittingRow.minPayload);
      const midPayloadStr = JSON.stringify(edittingRow.midPayload);
      const maxPayloadStr = JSON.stringify(edittingRow.maxPayload);
      
      const edittedScaleStr = `${minPayloadStr}${midPayloadStr}${maxPayloadStr}`;
      salaryScales[index]["salaryScaleId"] = data.id;
      
      if ("id" in salaryScales[index] && originalScale !== edittedScaleStr) {
         attachmentRef.current.showAttachmentModal(salaryScales[index]);
      } else {
         setLoadingBtn(true);
         delete salaryScales[index]["new"];
         createAndUpdateSalaryScaleRow(salaryScales[index])
         .then(() => {
            fetchData(year);
            setPresalaryScales([]);
         })
         .finally(() => setLoadingBtn(false))
      }

   }

   const onCancelButton = (index, salaryScale) => {
      if ("id" in salaryScale) {
         const found = presalaryScales.find((presalaryScale) => presalaryScale.id === salaryScale.id);
         const foundIndex = presalaryScales.findIndex((presalaryScale) => presalaryScale.id === salaryScale.id);

         salaryScales[index]["level"] = found.level;
         salaryScales[index]["jobTitles"] = found.jobTitles;
         salaryScales[index]["grade"] = found.grade;
         salaryScales[index]["minPayload"] = found.minPayload;
         salaryScales[index]["midPayload"] = found.midPayload;
         salaryScales[index]["maxPayload"] = found.maxPayload;
         salaryScales[index]["order"] = found.order;
         delete salaryScales[index]["new"];
         presalaryScales.splice(foundIndex, 1)
         setPresalaryScales([...presalaryScales])
         setSalaryScales([...salaryScales])

      } else {
         salaryScales.splice(index, 1)
         setSalaryScales([...salaryScales])
      }

   }

   const onChange = (key, index, value) => {
      salaryScales[index][key] = value;
      setSalaryScales([...salaryScales])
   }

   const onChangesalaryScale = (key, subKey, index, subIndex, value) => {
      salaryScales[index][key][subIndex][subKey] = value;
      setSalaryScales([...salaryScales])
   }

   const onHistory = () => {
      drawerRef.current.showHistoryDrawer(data.id);
   }

   const onDuplicate = () => {
      if (Util.getInstance().hasPermission("create", "salary_scale_record")) {
         modalRef.current.showDrawer()
      } else {
         Util.getInstance().sweetAlertMessage(t("text_no_permission"), "warning");
      }
   }

   useEffect(() => {
      setLoading(true);
      if (params.get("year")) {
         fetchData(params.get("year"));
      } else {
         fetchData(year);
         params.set("year", year);
         Util.getInstance().pushParamsToURL("/salary-scale-record", params.toString());
      }
      // eslint-disable-next-line
   }, []);

   return (
      <>
         {
            Util.getInstance().hasPermission("view", "salary_scale_record") ?
               <>
                  <Row style={styles.breadCream}>
                     <Col span={24}>
                        <Breadcrumb>
                           <Breadcrumb.Item
                              onClick={() => navigate("/")}
                              style={{ cursor: "pointer" }}
                           >
                              {t("text_dashboard")}
                           </Breadcrumb.Item>
                           <Breadcrumb.Item style={{ color: "#1d8747" }}>
                              {t("text_salary_scale_record")}
                           </Breadcrumb.Item>
                        </Breadcrumb>
                     </Col>
                  </Row>
                  {
                     loading ? <Spin spinning={loading} style={{ width: "100%", height: "84vh", display: "flex", justifyContent: "center", alignItems: "center" }} />
                        :
                        <>
                           <Row >
                              <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "15px 15px 0px 15px" }}>
                                 <div>
                                    <span style={{ fontSize: 16, fontWeight: 600, marginLeft: 8 }}>
                                       Salary Scale Record
                                    </span>
                                 </div>
                                 <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <SelectInput
                                       style={{ margin: 0, width: 100 }}
                                       onChange={onChangeYear}
                                       value={params.get("year") ? params.get("year") : year}
                                       placeholder={"Select year"}
                                       valueKey="id"
                                       dataSource={dataSourceYear}
                                    />
                                    <Button type="primary" icon={<PlusOutlined />} onClick={onAddNew} style={{ minWidth: "93px", marginLeft: 8 }}>{t("text_add_new")}</Button>
                                    {data.id && <Button icon={<CopyOutlined />} type="default" style={{ minWidth: "93px", marginLeft: 8 }} onClick={onDuplicate}>{t("text_duplicate")}</Button>}
                                    <Button type="default" icon={<HistoryOutlined />} onClick={onHistory} style={{ minWidth: "93px", marginLeft: 8 }}>History</Button>
                                 </div>
                              </div>
                           </Row>
                           <HistoryDrawer ref={drawerRef} />
                           <AttachmentModal ref={attachmentRef} datas={{ data, setPresalaryScales: setPresalaryScales, fetchData: fetchData }} />
                           <DuplicateDrawer ref={modalRef} datas={{ data, setYear: setYear, fetchData: fetchData }} />
                           <Row style={{ marginTop: 15 }}>

                              <Col span={24}>
                                 {
                                    data.id ?
                                       <Spin spinning={loadingTable} style={{ width: "100%", height: "84vh", display: "flex", justifyContent: "center", alignItems: "center" }} >
                                          <div style={{ padding: "0 15px" }} className="tableFixHead" >
                                             <table id="salary-scale">
                                                <thead style={{
                                                   position: "sticky",
                                                   top: -1,
                                                   zIndex: 1
                                                }}>
                                                   <tr>
                                                      <th rowSpan={2} style={{ background: "rgb(146, 208, 80)", width: 150 }}>Levels</th>
                                                      <th rowSpan={2} style={{ background: "rgb(146, 208, 80)" }}>JOB TITLE</th>
                                                      <th rowSpan={2} style={{ width: 60, background: "rgb(255, 255, 0)" }}>Grade</th>
                                                      <th colSpan={data.minColumns.length} style={{ background: "rgb(255, 255, 0)" }}>Min</th>
                                                      <th colSpan={data.midColumns.length} style={{ background: "rgb(255, 255, 0)" }}>Mid</th>
                                                      <th colSpan={data.maxColumns.length} style={{ background: "rgb(255, 255, 0)" }}>Max</th>
                                                      <th rowSpan={2} style={{ width: 50 }}>Order</th>
                                                      <th rowSpan={2} style={{ width: 90 }}>Action</th>
                                                   </tr>

                                                   <tr>
                                                      {
                                                         data.minColumns.map((_, index) => <th key={`minColumns${index}`} style={{ width: 60, background: "rgb(255, 255, 0)" }}>{index + 1}</th>)
                                                      }
                                                      {
                                                         data.midColumns.map((_, index) => <th key={`midColumns${index}`} style={{ width: 60, background: "rgb(255, 255, 0)" }}>{data.minColumns.length + (index + 1)}</th>)
                                                      }
                                                      {
                                                         data.maxColumns.map((_, index) => <th key={`maxColumns${index}`} style={{ width: 60, background: "rgb(255, 255, 0)" }}>{(data.minColumns.length + data.midColumns.length) + (index + 1)}</th>)
                                                      }
                                                   </tr>
                                                </thead>
                                                <tbody>
                                                   {

                                                      salaryScales.map((salaryScale, index) => {
                                                         return <tr key={salaryScale.key ? salaryScale.key : salaryScale.id} className={salaryScale.status && "hidded-custom-row"}>
                                                            <td>
                                                               {
                                                                  !salaryScale.status &&
                                                                  (salaryScale.new ?
                                                                     <InputText
                                                                        defaultValue={salaryScale.level}
                                                                        onChange={(event) => onChange("level", index, event.target.value)}
                                                                     />
                                                                     :
                                                                     <p style={{ paddingLeft: 7, margin: 0 }}>{salaryScale.level}</p>
                                                                  )
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  !salaryScale.status &&
                                                                  (salaryScale.new ?
                                                                     <Input.TextArea
                                                                        defaultValue={salaryScale.jobTitles}
                                                                        onChange={(event) => onChange("jobTitles", index, event.target.value)}
                                                                     />
                                                                     :
                                                                     <p style={{ paddingLeft: 7, margin: 0 }}>{salaryScale.jobTitles}</p>

                                                                  )
                                                               }
                                                            </td>
                                                            <td>
                                                               {
                                                                  !salaryScale.status &&
                                                                  (salaryScale.new ?
                                                                     <InputText
                                                                        defaultValue={salaryScale.grade}
                                                                        onChange={(event) => onChange("grade", index, event.target.value)}
                                                                     />
                                                                     :
                                                                     <span className="display-center">{salaryScale.grade}</span>

                                                                  )
                                                               }
                                                            </td>
                                                            {salaryScale.minPayload.map((minPay, indexMinPay) => {
                                                               return <td key={`minPay${indexMinPay}`}>
                                                                  {
                                                                     !salaryScale.status &&
                                                                     (salaryScale.new ?
                                                                        <InputText
                                                                           defaultValue={minPay[indexMinPay + 1]}
                                                                           onChange={(event) => onChangesalaryScale("minPayload", indexMinPay + 1, index, indexMinPay, event.target.value)}
                                                                        />
                                                                        :
                                                                        <span className="display-center">{minPay[indexMinPay + 1]}</span>

                                                                     )
                                                                  }
                                                               </td>
                                                            })}
                                                            {salaryScale.midPayload.map((midPay, indexMidPay) => {
                                                               return <td key={`midPayload${indexMidPay}`}>
                                                                  {
                                                                     !salaryScale.status &&
                                                                     (salaryScale.new ?
                                                                        <InputText
                                                                           defaultValue={midPay[indexMidPay + 1]}
                                                                           onChange={(event) => onChangesalaryScale("midPayload", indexMidPay + 1, index, indexMidPay, event.target.value)}
                                                                        />
                                                                        :
                                                                        <span className="display-center">{midPay[indexMidPay + 1]}</span>

                                                                     )
                                                                  }
                                                               </td>
                                                            })}
                                                            {salaryScale.maxPayload.map((maxPay, indexMaxPay) => {
                                                               return <td key={`maxPay${indexMaxPay}`}>
                                                                  {
                                                                     !salaryScale.status &&
                                                                     (salaryScale.new ?
                                                                        <InputText
                                                                           defaultValue={maxPay[indexMaxPay + 1]}
                                                                           onChange={(event) => onChangesalaryScale("maxPayload", indexMaxPay + 1, index, indexMaxPay, event.target.value)}
                                                                        />
                                                                        :
                                                                        <span className="display-center">{maxPay[indexMaxPay + 1]}</span>

                                                                     )
                                                                  }
                                                               </td>
                                                            })}
                                                            <td>
                                                               {
                                                                  !salaryScale.status &&
                                                                  (salaryScale.new ?
                                                                     <InputText
                                                                        defaultValue={salaryScale.order}
                                                                        onChange={(event) => onChange("order", index, event.target.value)}
                                                                     />
                                                                     :
                                                                     <span className="display-center">{salaryScale.order}</span>
                                                                  )
                                                               }
                                                            </td>

                                                            <td>
                                                               {
                                                                  !salaryScale.status &&
                                                                  (salaryScale.new ?

                                                                     <Space style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                                        <Button
                                                                           style={{
                                                                              marginTop: 0,

                                                                              height: 35,
                                                                              width: 35,
                                                                              borderRadius: 35,
                                                                              border: "none"
                                                                           }}
                                                                           type="primary"
                                                                           title={t("text_save")}
                                                                           loading={loadingBtn}
                                                                           onClick={() => onSaveButton(index, salaryScale)}
                                                                           icon={<CheckOutlined style={{ color: "#fff" }} />}
                                                                        />
                                                                        <Button
                                                                           style={{
                                                                              marginTop: 0,
                                                                              height: 35,
                                                                              width: 35,
                                                                              borderRadius: 35,
                                                                              border: "none"
                                                                           }}
                                                                           type="danger"
                                                                           shape="circle"
                                                                           onClick={() => onCancelButton(index, salaryScale)}
                                                                           icon={<RedoOutlined style={{ color: "#fff" }} />}
                                                                        />

                                                                     </Space>

                                                                     :
                                                                     <Space style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                                        <Button
                                                                           style={{
                                                                              marginTop: 0,
                                                                              height: 35,
                                                                              width: 35,
                                                                              borderRadius: 35,
                                                                              border: "none"
                                                                           }}
                                                                           type="primary"
                                                                           title={`${t("text_update")}`}
                                                                           onClick={() => onUpdateButton(index, { ...salaryScale })}
                                                                           icon={<EditFilled style={{ color: "#fff" }} />}
                                                                        />

                                                                        <Button
                                                                           danger
                                                                           title={t("text_delete")}
                                                                           icon={<DeleteFilled style={{ color: "#fff" }} />}
                                                                           shape="circle"
                                                                           type="primary"
                                                                           style={{
                                                                              height: 35,
                                                                              width: 35,
                                                                              borderRadius: 35,
                                                                              border: "none",
                                                                              marginTop: 0,
                                                                           }}
                                                                           onClick={() => onDeleteButton(index)}
                                                                        />
                                                                     </Space>)

                                                               }

                                                            </td>
                                                         </tr>
                                                      })

                                                   }
                                                   <tr>
                                                      <td colSpan={(data.minColumns.length + data.midColumns.length + data.maxColumns.length + 8)}>
                                                         <Button type="dashed" icon={<PlusOutlined />} style={{ width: "100%" }} onClick={onNew}>
                                                            {t("text_add_new")}
                                                         </Button>
                                                      </td>
                                                   </tr>
                                                </tbody>
                                             </table>
                                          </div>
                                       </Spin>
                                       :
                                       <Result
                                          status="404"
                                          title="404"
                                          subTitle="Sorry, the page you visited does not exist."
                                       />
                                 }
                              </Col>
                           </Row>
                        </>
                  }
               </>
               :
               <Result
                  status="403"
                  title="403"
                  subTitle={t("text_sorry_you_are_not_authorized_to_access_this_page")}
               />

         }


      </>
   )
}

const styles = {
   breadCream: {
      paddingBottom: 15,
      backgroundColor: "#EFF3F6",
   },
};
